import { useMemo, type SetStateAction } from 'react'
import clsx from 'clsx'
import { useNavigate } from 'react-router-dom'
import { SortableList, SortableItem } from '@thaddeusjiang/react-sortable-list'
import '@thaddeusjiang/react-sortable-list/dist/index.css'
import { KTIcon } from '../../../../../../_metronic/helpers'
import type { CoverageEntry } from '../model/coverage'
import { encodeContextId } from '../model/context'
import { StatusBadge } from './StatusBadge'
import { FlagChip } from './FlagChip'
import { contextLabel, formatRub, formatUpdatedAt } from '../labels'

interface Props {
  /** Already sorted (active first, disabled bottom). */
  configurations: CoverageEntry[]
  merchantTitle: (token: string) => string
  /** Persist new manual order after a drag. */
  onReorder: (next: CoverageEntry[]) => void
  /** Drag disabled while a status filter is active (order is meaningless then). */
  dragDisabled: boolean
}

interface SortableEntry extends CoverageEntry {
  /** id required by the sortable list. */
  id: string
}

const DragHandler = (props: any) => (
  <div title='Переместить' {...props} className='me-3 text-gray-400' style={{ cursor: 'grab' }}>
    <KTIcon iconName='abstract-14' className='fs-2' />
  </div>
)

/**
 * Configuration list grouped visually by merchant header rows, drag-reorderable
 * (CHC-869 UX fix #3). Disabled configurations are forced to the bottom by the
 * parent's sort and rendered muted; dragging them above active ones has no
 * effect because the sort re-pins them.
 */
export const CoverageList = ({ configurations, merchantTitle, onReorder, dragDisabled }: Props) => {
  const navigate = useNavigate()

  const items: SortableEntry[] = useMemo(
    () => configurations.map((c) => ({ ...c, id: encodeContextId(c.context) })),
    [configurations]
  )

  const open = (entry: CoverageEntry) =>
    navigate(`/settings/adapter-routing/config/${encodeContextId(entry.context)}`)

  if (configurations.length === 0) {
    return (
      <div className='text-center text-gray-500 py-10'>Нет конфигураций в этом разделе.</div>
    )
  }

  // The sortable list calls setItems with a functional updater
  // (prev => arrayMove(prev, ...)). Resolve it against the current items, then
  // strip the synthetic id and hand the ordered entries back up.
  const setItems = (next: SetStateAction<SortableEntry[]>) => {
    const resolved = typeof next === 'function' ? next(items) : next
    onReorder(resolved.map(({ id, ...rest }) => rest))
  }

  return (
    <SortableList
      disabled={dragDisabled}
      items={items as any}
      // @ts-ignore — library setItems typing is loose, matches existing screens
      setItems={setItems}
    >
      {({ items: rendered }: any) => (
        <div className='d-flex flex-column gap-3'>
          {(rendered as SortableEntry[]).map((entry) => {
            const isDisabled = entry.status === 'DISABLED'
            return (
              <SortableItem
                key={entry.id}
                id={entry.id}
                DragHandler={dragDisabled ? undefined : DragHandler}
                className='d-flex align-items-stretch'
              >
                <div
                  className={clsx('card w-100 border', isDisabled && 'opacity-50')}
                  style={{ borderColor: 'var(--bs-border-color)' }}
                >
                  <div className='card-body d-flex flex-wrap align-items-center justify-content-between p-4 gap-3'>
                    <div className='d-flex flex-column'>
                      <div className='d-flex align-items-center gap-2 mb-1'>
                        <span className='fw-bold fs-5 text-gray-900'>
                          {merchantTitle(entry.context.merchantToken)}
                        </span>
                        <StatusBadge status={entry.status} />
                        <FlagChip flag={entry.context.externalUserFlag} />
                      </div>
                      <span className='text-gray-600 fs-7'>{contextLabel(entry.context)}</span>
                    </div>

                    <div className='d-flex flex-wrap align-items-center gap-6'>
                      <Fact
                        label='Адаптеров'
                        value={`${entry.enabledAdapterCount}/${entry.totalAdapterCount}`}
                      />
                      <Fact
                        label='Диапазон'
                        value={
                          entry.coverage.minCovered === null
                            ? '—'
                            : `${formatRub(entry.coverage.minCovered)} – ${
                                entry.coverage.maxCovered === null
                                  ? '∞'
                                  : formatRub(entry.coverage.maxCovered)
                              }`
                        }
                      />
                      <Fact label='Обновлено' value={formatUpdatedAt(entry.updatedAt)} />
                      <button
                        type='button'
                        className='btn btn-sm btn-light-primary'
                        onClick={() => open(entry)}
                      >
                        Открыть
                        <KTIcon iconName='arrow-right' className='fs-5 ms-1' />
                      </button>
                    </div>
                  </div>
                </div>
              </SortableItem>
            )
          })}
        </div>
      )}
    </SortableList>
  )
}

const Fact = ({ label, value }: { label: string; value: string }) => (
  <div className='d-flex flex-column'>
    <span className='text-gray-500 fs-8 text-uppercase'>{label}</span>
    <span className='text-gray-800 fw-semibold fs-7'>{value}</span>
  </div>
)
